import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import './NotificationSystem.css';
import { FaTimes, FaTrophy, FaStar, FaFire, FaBell } from 'react-icons/fa';

const NotificationSystem = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [levelUpAnimation, setLevelUpAnimation] = useState(null);
  const { userInfo } = useSelector(state => state.userLogin);
  const user = userInfo;

  useEffect(() => {
    if (user && user.token) {
      fetchNotifications();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchNotifications = async () => {
    if (!user?.token) return;
    
    try {
      const response = await axios.get('https://api.andrewslearning.com/api/notifications/', {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
      
      // Check for new level up notifications to trigger animation
      // Only show animation for notifications created in the last 2 minutes
      const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000);
      const newLevelUpNotifications = response.data.notifications.filter(
        notif => notif.type === 'level_up' && 
                 !notif.is_read && 
                 new Date(notif.created_at) > twoMinutesAgo
      );
      
      if (newLevelUpNotifications.length > 0) {
        const levelUpNotif = newLevelUpNotifications[0];
        setLevelUpAnimation(levelUpNotif);
        setTimeout(() => setLevelUpAnimation(null), 5000);
      }
      
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    if (!user?.token) return;
    
    try {
      await axios.post(
        `https://api.andrewslearning.com/api/notifications/${notificationId}/read/`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, is_read: true, read_at: new Date().toISOString() }
            : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user?.token) return;
    
    try {
      await axios.post(
        'https://api.andrewslearning.com/api/notifications/mark-all-read/',
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, is_read: true, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
      
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'level_up': return <FaTrophy className="notification-icon level-up" />;
      case 'achievement': return <FaStar className="notification-icon achievement" />;
      case 'streak_milestone': return <FaFire className="notification-icon streak" />;
      default: return <FaBell className="notification-icon default" />;
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - notifTime) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return notifTime.toLocaleDateString();
  };

  return (
    <div className="notification-system">
      {/* Level Up Animation Overlay */}
      {levelUpAnimation && (
        <div className="level-up-overlay">
          <div className="level-up-animation">
            <div className="level-up-content">
              <FaTrophy className="level-up-trophy" />
              <h2 className="level-up-title">{levelUpAnimation.title}</h2>
              <p className="level-up-message">{levelUpAnimation.message}</p>
              <div className="level-up-sparkles">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className={`sparkle sparkle-${i + 1}`}>✨</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Bell */}
      <div className="notification-bell-container">
        <button 
          className="notification-bell"
          onClick={() => setShowNotifications(!showNotifications)}
        >
          <FaBell />
          {unreadCount > 0 && (
            <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </button>

        {/* Notification Panel */}
        {showNotifications && (
          <div className="notification-panel">
            <div className="notification-header">
              <h3>Notifications</h3>
              {unreadCount > 0 && (
                <button className="mark-all-read" onClick={markAllAsRead}>
                  Mark all read
                </button>
              )}
              <button 
                className="close-notifications"
                onClick={() => setShowNotifications(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="notification-list">
              {notifications.length === 0 ? (
                <div className="no-notifications">
                  <FaBell />
                  <p>No notifications yet</p>
                </div>
              ) : (
                notifications.map(notification => (
                  <div 
                    key={notification.id}
                    className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                    onClick={() => !notification.is_read && markAsRead(notification.id)}
                  >
                    {getNotificationIcon(notification.type)}
                    <div className="notification-content">
                      <h4 className="notification-title">{notification.title}</h4>
                      <p className="notification-message">{notification.message}</p>
                      <span className="notification-time">
                        {formatTimeAgo(notification.created_at)}
                      </span>
                    </div>
                    {!notification.is_read && <div className="unread-dot"></div>}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationSystem;